import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showSnackbar } from "../../../api/snackbarSlice";
import { useDispatch } from "react-redux";
import { apiClient } from "../../../utils/axios";
import { isString } from "lodash";
import { useState } from "react";
import { AxiosError } from "axios";

const resubmitSalesCreditNote = async ({ payload, creditNoteUuid }) => {
  const profileId = payload?.accounting_supplier_party?.profile_id;
  if (!profileId) {
    throw new Error(
      "Supplier Profile ID is required to resubmit a sales credit note",
    );
  }
  const response = await apiClient.put(
    `/sales-credit-notes/${creditNoteUuid}/${profileId}`,
    payload,
  );
  return response.data;
};

const resubmitPurchaseCreditNote = async ({ payload, creditNoteUuid }) => {
  const profileId = payload?.accounting_customer_party?.profile_id;
  if (!profileId) {
    throw new Error(
      "Customer Profile ID is required to resubmit a purchase credit note",
    );
  }
  const response = await apiClient.put(
    `/purchase-credit-notes/${creditNoteUuid}/${profileId}`,
    payload,
  );
  return response.data;
};

export const useResubmitCreditNoteHandler = ({
  onSuccess,
  isPurchase = false,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: isPurchase
      ? resubmitPurchaseCreditNote
      : resubmitSalesCreditNote,
    onSuccess: (data, variables) => {
      dispatch(
        showSnackbar({ message: "Credit note resubmitted successfully." }),
      );
      queryClient.invalidateQueries({
        queryKey: [isPurchase ? "purchase-credit-notes" : "sales-credit-notes"],
      });
      onSuccess && onSuccess(variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          setError(error.response.data.errors);
          return;
        }
        dispatch(
          showSnackbar({
            message: error.response?.data?.message ?? error.message,
            severity: "error",
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            message: isString(error.message)
              ? error.message
              : JSON.stringify(error.message),
            severity: "error",
          }),
        );
      }
    },
  });

  const onSubmit = async (payload, creditNoteUuid) => {
    const profileId = isPurchase
      ? payload?.accounting_customer_party?.profile_id
      : payload?.accounting_supplier_party?.profile_id;
    if (!profileId) {
      dispatch(
        showSnackbar({
          message: isPurchase
            ? "Customer Profile ID is required to resubmit a purchase credit note"
            : "Supplier Profile ID is required to resubmit a sales credit note",
          severity: "error",
        }),
      );
      return;
    }
    mutation.mutate({ payload, creditNoteUuid });
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit: onSubmit,
  };
};
